/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect } from 'react';
import {
  Checkbox,
  Divider,
  Grid,
  Icon,
  Input,
  Select,
  Tree
} from '@scuf/common';
import AlarmThresholdForm from './AlarmThresholdForm';
import {
  IAlarmSaveRequest,
  ICreateMeterAlarmProps,
  IMeters,
  ISelection,
  ISiteMeter,
  ISiteMeters,
  IThresholdRequest
} from './IAlarmConfiguration';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../locales/en-US/descriptors';
import '../../scss/AlarmConfigurationsVariables.scss';
import { alarmTypes, EMPTY_GUID } from '../shared/Utils/Constants';
import { withLocalization } from '../../locales/withLocalization';

const CreateMeterAlarm = (props: ICreateMeterAlarmProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [siteMeters, setSiteMeters] = React.useState<ISiteMeters[]>([]);
  const [alarmType, setAlarmType] = React.useState('');
  const [templateId, setTemplateId] = React.useState('');
  const [selections, setSelections] = React.useState<ISelection[]>([]);

  const d = descriptors.alarmConfiguration;
  const [t] = useTranslation();

  const { organizationName } = props;
  const Item = Tree.Content.Item;

  useEffect(() => {
    if (
      siteMeters.length === 1 &&
      !siteMeters[0].Expanded &&
      siteMeters[0].Meters.length === 0
    ) {
      handleSiteExpansion(siteMeters[0]);
    }
  }, [siteMeters]);

  useEffect(() => {
    loadSitesAndMeters();
    setSelections([]);
  }, [organizationName]);

  const handleSiteExpansion = (item: ISiteMeters) => {
    const index = siteMeters.indexOf(item);
    const siteMetersCopy = [...siteMeters];
    const updatedMeterData: ISiteMeters = {
      ...siteMetersCopy[index],
      Expanded: !siteMetersCopy[index].Expanded,
      IsLoading: false
    };
    if (updatedMeterData.Expanded && updatedMeterData.Meters.length === 0) {
      updatedMeterData.IsLoading = true;
      props
        .loadMetersForSite(alarmType, item.SiteId)
        .then((response: ISiteMeter[]) => {
          const siteMetersForSiteCopy = [...siteMeters];
          const Meters = response.map(
            (resource: ISiteMeter, key: number): IMeters => ({
              MeterId: resource.id,
              MeterName: resource.name,
              Selected: siteMetersForSiteCopy[index].Selected
            })
          );
          if (Meters.length === 0) {
            updatedMeterData.Meters = [
              {
                MeterId: EMPTY_GUID,
                MeterName: t(d.NOMETERSUNDERSITE),
                Selected: false
              }
            ];
          } else {
            updatedMeterData.Meters = Meters;
          }
          updatedMeterData.IsLoading = false;
          siteMetersForSiteCopy[index] = updatedMeterData;
          setSiteMeters(siteMetersForSiteCopy);
        });
    }

    siteMetersCopy[index] = updatedMeterData;
    setSiteMeters(siteMetersCopy);
  };

  const handleMeterSelection = (
    siteId: string,
    siteName: string,
    meter?: IMeters
  ) => {
    const siteIndex = siteMeters.findIndex((item) => item.SiteId === siteId);
    const siteMetersCopy = [...siteMeters];
    if (meter) {
      const metersCopy = [...siteMetersCopy[siteIndex].Meters];
      const meterIndex = metersCopy.findIndex((item) => item === meter);
      metersCopy[meterIndex].Selected = !metersCopy[meterIndex].Selected;
      let selected = false;
      if (metersCopy.filter((item) => item.Selected).length > 0) {
        selected = siteMetersCopy[siteIndex].Selected;
      }
      siteMetersCopy[siteIndex] = {
        ...siteMetersCopy[siteIndex],
        Selected: selected,
        Meters: [...metersCopy]
      };
    } else {
      siteMetersCopy[siteIndex].Selected = !siteMetersCopy[siteIndex].Selected;
      siteMetersCopy[siteIndex].Meters.forEach(
        (_meter) => (_meter.Selected = siteMetersCopy[siteIndex].Selected)
      );
    }
    setSiteMeters(siteMetersCopy);

    const selectionsCopy = [...selections];
    const selectionsIndex = selectionsCopy.findIndex(
      (item) => item.SiteId === siteId
    );
    const selectedMeters = siteMetersCopy[siteIndex].Meters.filter(
      (item) => item.Selected
    ).map((item) => ({
      MeterId: item.MeterId,
      MeterName: item.MeterName
    }));
    if (selectionsIndex >= 0) {
      if (selectedMeters.length > 0) {
        selectionsCopy[selectionsIndex] = {
          ...selectionsCopy[selectionsIndex],
          Elements: selectedMeters
        };
      } else {
        selectionsCopy.splice(selectionsIndex, 1);
      }
    } else {
      selectionsCopy.push({
        SiteId: siteId,
        TemplateId: templateId,
        SiteName: siteName,
        Elements: selectedMeters
      });
    }
    setSelections(selectionsCopy);
  };

  const handleAlarmTypeChange = (value: string) => {
    setAlarmType(value);
    setTemplateId(alarmTypes.find((r) => r.value === value)?.templateId || '');
    setSelections([]);
    loadSitesAndMeters();
  };

  const loadSitesAndMeters = async () => {
    setSitesLoading(true);
    let _siteMeters: ISiteMeters[];
    if (props.isRootASite) {
      _siteMeters = [
        {
          SiteId: props.rootNodeId || '',
          SiteName: organizationName,
          Expanded: false,
          Selected: false,
          IsLoading: false,
          Meters: []
        }
      ];
    } else {
      const sites = await props.loadSites();
      _siteMeters = sites.map((item: ISiteMeter) => ({
        SiteId: item.id,
        SiteName: item.name,
        Expanded: false,
        Selected: false,
        IsLoading: false,
        Meters: []
      }));
    }
    setSiteMeters(_siteMeters);
    setSitesLoading(false);
  };

  const handleAlarmConfigurationSave = (values: IThresholdRequest[]) => {
    const request: IAlarmSaveRequest = {
      alarmType: alarmType,
      elements: selections,
      configurations: values
    };
    props.saveAlarmConfiguration(request);
  };

  const getAlarmDescriptionText = () => {
    if (!!alarmType)
      return (
        <span className="alarm-type-subtext">
          {t(d.CONFIGUREALARMDESCLINE2)}
        </span>
      );
    else
      return (
        <span className="alarm-type-subtext">
          {t(d.CONFIGUREALARMDESCLINE1)}
        </span>
      );
  };

  const renderMeterIcon = () => {
    switch (alarmType) {
      case 'ElectricMeter':
        return (
          <img
            className="alarm-meter-icon"
            src={require('../../images/Energy-icon.svg')}
          />
        );
      case 'GasMeter':
      default:
        return (
          <img
            className="alarm-meter-icon"
            src={require('../../images/GasIcon.svg')}
          />
        );
    }
  };

  const renderSiteMetersOrMetersBasedOnRootNode = () => {
    if (props.isRootASite) {
      return renderMetersUnderSite();
    } else {
      return (
        <Item
          expanded
          isLoading={sitesLoading}
          title={
            <>
              <Checkbox disabled indeterminate={selections.length > 0} />
              <span>{organizationName}</span>
            </>
          }
        >
          {renderSubTreeItems()}
        </Item>
      );
    }
  };

  const renderSubTreeItems = () => {
    if (siteMeters && siteMeters.length > 0) return renderMetersUnderSite();
    else return <div className="no-sites">{t(d.NOSITESAVAILABLE)}</div>;
  };

  const renderMetersUnderSite = () => {
    return siteMeters.map((site, siteKey) => {
      return (
        <Item
          key={siteKey}
          isLoading={site.IsLoading}
          expanded={site.Expanded}
          onClick={() => handleSiteExpansion(site)}
          className="custom-tree-item"
          title={
            <>
              {site.Meters &&
                site.Meters.length > 0 &&
                site.Meters[0].MeterId !== EMPTY_GUID && (
                  <Checkbox
                    disabled
                    indeterminate={
                      site.Meters.filter((m) => m.Selected).length > 0 &&
                      site.Meters.filter((m) => m.Selected).length <
                        site.Meters.length
                    }
                    checked={
                      site.Selected ||
                      site.Meters.filter((m) => m.Selected).length ===
                        site.Meters.length
                    }
                  />
                )}
              <span className="meter-count">
                {site.SiteName}{' '}
                {site.Meters &&
                  site.Meters.length > 0 &&
                  site.Meters[0].MeterId !== EMPTY_GUID &&
                  ` (${site.Meters.length})`}
              </span>
            </>
          }
        >
          {site.Meters.length > 0 ? (
            site.Meters.map((meter, mKey) => {
              return meter.MeterName.toLowerCase().includes(
                searchTerm.toLowerCase()
              ) ? (
                <Item
                  className="custom-sub-tree-item"
                  key={mKey}
                  title={
                    <>
                      {meter.MeterId !== EMPTY_GUID && (
                        <>
                          <Checkbox
                            checked={meter.Selected}
                            onChange={() =>
                              handleMeterSelection(
                                site.SiteId,
                                site.SiteName,
                                meter
                              )
                            }
                          />
                          {renderMeterIcon()}
                        </>
                      )}
                      <span>{meter.MeterName}</span>
                    </>
                  }
                />
              ) : (
                <></>
              );
            })
          ) : (
            <></>
          )}
        </Item>
      );
    });
  };

  return (
    <div className="emaas-create-alarm-section">
      <Grid>
        <Grid.Row>
          <Grid.Column width={12} className="alarm-type-header">
            <h4>{t(d.ALARMTYPE)}</h4>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <Select
              fluid
              value={alarmType}
              placeholder={t(d.SEARCHALARMTYPE)}
              options={alarmTypes.map((type) => ({
                text: type.text,
                value: type.value
              }))}
              onChange={(value) => handleAlarmTypeChange(value)}
            />
          </Grid.Column>
          <Grid.Column width={9}>{getAlarmDescriptionText()}</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={12} className="divider divider-row">
            <Divider className="divider-no-margin" />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {!!alarmType && (
        <AlarmThresholdForm
          header={
            alarmTypes.find((i) => i.value === alarmType)?.header +
            ' ' +
            t(d.THRESHOLDSETTINGS)
          }
          isMetersSelected={selections.length > 0}
          onSave={handleAlarmConfigurationSave}
        >
          <Tree>
            <Tree.Header>
              <div className="search-textbox">
                <Input
                  fluid
                  iconPosition="left"
                  placeholder={t(d.SEARCHMETERS)}
                  icon={<Icon root="common" name="search" size="small" />}
                  value={searchTerm}
                  onChange={(text) => setSearchTerm(text)}
                />
              </div>
            </Tree.Header>
            <Tree.Content maxHeight={375}>
              {renderSiteMetersOrMetersBasedOnRootNode()}
            </Tree.Content>
          </Tree>
        </AlarmThresholdForm>
      )}
    </div>
  );
};

export default withLocalization<ICreateMeterAlarmProps>(CreateMeterAlarm);
